// Dynamic stage-color helpers for Owner Dashboard.
// Pure colour-mapping — does NOT change counts, percents, labels, or data.
//
// 2026-02-27 Executive Color System — per-stage palette adjusted to
// match the "improved dashboard" target:
//   Case lifecycle widget:
//     Lead          → Blue   (#2563EB)
//     Quotation     → Teal   (#14B8A6)
//     Customer      → Indigo (#4F46E5)
//     Case Open     → Green  (#16A34A)
//   Sales funnel widget:
//     Leads         → Light Blue (#60A5FA)
//     MQL           → Blue       (#2563EB)
//     Quoted        → Teal       (#14B8A6)
//     Won           → Green      (#16A34A)
//     Invoiced      → Dark Navy  (#1E293B)
// Chart proportions, layouts, and counts remain untouched.

const COLORS = {
  red: "#DC2626",
  amber: "#F59E0B",
  orange: "#F97316",
  green: "#16A34A",
  blue: "#2563EB",
  lightBlue: "#60A5FA",
  teal: "#14B8A6",
  indigo: "#4F46E5",
  darkNavy: "#1E293B",
  slate: "#64748B",
  track: "#F1F5F9",
};

export function normalizeStage(value) {
  if (value == null) return "";
  let s;
  if (typeof value === "string") s = value;
  else if (typeof value === "object") s = value.key || value.label || value.name || "";
  else s = String(value);
  return s.toString().trim().toLowerCase().replace(/[\s/]+/g, "_").replace(/[^a-z0-9_]/g, "");
}

// Active WIP signal — used by lifecycle bars to visually emphasise
// in-flight work (slightly taller bar + inner-shadow). We keep the
// previous behaviour: only "case open" / submissions / approvals
// surface as active.
const ACTIVE_KEYS = new Set([
  "case", "case_open", "caseopen", "open", "submission", "submissions",
  "approval", "approvals", "negotiation",
]);

// ---- Case lifecycle ------------------------------------------------------
export function getLifecycleStageColor(stage) {
  const n = normalizeStage(stage);
  if (!n) return COLORS.slate;
  if (n === "lead" || n === "leads" || n === "mql" || n.startsWith("qualified")) return COLORS.blue;
  if (n.includes("quotation") || n.includes("quote") || n === "quoted") return COLORS.teal;
  if (n === "customer" || n === "customers" || n.includes("onboarding")) return COLORS.indigo;
  if (n.includes("case") || n === "open" || n.includes("submission") || n.includes("approv")
      || n === "won") return COLORS.green;
  // Invoice / receipt nodes still surface but pick up the darker navy.
  if (n.includes("invoice") || n.includes("receipt") || n.includes("paid") || n.includes("collect"))
    return COLORS.darkNavy;
  return COLORS.slate;
}

// ---- Sales funnel --------------------------------------------------------
export function getFunnelStageColor(stage) {
  const n = normalizeStage(stage);
  if (!n) return COLORS.slate;
  if (n === "lead" || n === "leads") return COLORS.lightBlue;
  if (n === "mql" || n.startsWith("qualified")) return COLORS.blue;
  if (n.includes("quote") || n === "quoted" || n.includes("quotation")) return COLORS.teal;
  if (n === "won" || n.includes("accepted")) return COLORS.green;
  if (n.includes("invoice") || n.includes("invoiced") || n.includes("paid") || n.includes("receipt"))
    return COLORS.darkNavy;
  if (n === "customer" || n === "customers") return COLORS.indigo;
  return COLORS.slate;
}

export function isActiveWipStage(stage) {
  return ACTIVE_KEYS.has(normalizeStage(stage));
}

export const DASHBOARD_COLORS = COLORS;
